import classNames from 'classnames';
import { ReactNode } from 'react';

import './DataTable.scss';

export type DataTableColumnConfig<T> = {
    /** Unique column key */
    key: string;
    /** Column header label */
    heading: string;
    /**
     * Extracts the raw cell value from a row — rendered as-is.
     */
    value?: (row: T) => ReactNode;
    /**
     * Optional override — use when the cell needs custom JSX.
     * When provided, `value` is ignored for this column.
     */
    renderCell?: (row: T) => ReactNode;
};

export type DataTableProps<T> = {
    /** Column definitions */
    columns: DataTableColumnConfig<T>[];
    /** Row data */
    rows: T[];
    /** Key extractor for rows */
    getRowKey: (row: T) => string;
    /** Optional row click handler */
    onRowClick?: (row: T) => void;
    /** Extra class name on the outer wrapper */
    className?: string;
};

export const DataTable = <T,>({ columns, rows, getRowKey, onRowClick, className }: DataTableProps<T>) => (
    <div className={classNames('data-table-wrapper', className)}>
        <div className="data-table-container">
            <table className="data-table">
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col.key}>
                                <div className="data-table__heading">
                                    <span>{col.heading}</span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={getRowKey(row)}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            className={classNames({ 'data-table__row--clickable': !!onRowClick })}
                        >
                            {columns.map((col) => (
                                <td key={col.key}>{col.renderCell ? col.renderCell(row) : col.value?.(row)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);
